import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams, Link} from 'react-router-dom';
import styled from 'styled-components';
import {Alert, Button, Card, Dialog} from '@betareader/design-system';
import {getSample, rateSample, revokeContact} from './api';

const Page = styled.div`
  padding: 20px;
  max-width: 750px;
  margin: 0 auto;
`;

const ChapterText = styled.div`
  font-size: 1.05em;
  line-height: 1.7;
  white-space: pre-wrap;
  margin-top: 16px;
`;

const StickyCta = styled.div`
  position: sticky;
  bottom: 0;
  padding: 12px;
  background: var(--br-surface);
  border-top: 1px solid var(--br-border);
  text-align: center;
  z-index: 10;
`;

const Note = styled.div`
  font-size: 0.85em;
  color: var(--br-muted);
  margin-top: 8px;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--br-space-2);
  margin-top: var(--br-space-2);
`;

const Tag = styled.span`
  background: var(--br-raised-surface);
  border: 1px solid var(--br-border);
  border-radius: 999px;
  padding: var(--br-space-1) var(--br-space-2);
`;

const Section = styled(Card)`
  margin-top: var(--br-space-4);
`;

const AnswerForm = styled.div`
  display: grid;
  gap: var(--br-space-3);

  textarea {
    background: var(--br-surface);
    border: 1px solid var(--br-border);
    border-radius: var(--br-control-radius);
    color: var(--br-text);
    display: block;
    margin-top: var(--br-space-2);
    padding: var(--br-space-2);
    width: 100%;
  }
`;

const SampleChapterReader = () => {
  const {id} = useParams();
  const idToken = useSelector((s) => s.user.idToken);
  const [sample, setSample] = useState(null);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAnswer, setPendingAnswer] = useState(null); // 'yes' | 'no'
  const [contactOptIn, setContactOptIn] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const startedAt = useRef(Date.now());

  const load = async () => {
    try {
      const s = await getSample(idToken, id);
      setSample(s);
      setError(null);
      if (s.myRating) {
        setContactOptIn(
          !!s.myRating.contactOptIn && !s.myRating.contactRevokedAt
        );
        setComment(s.myRating.comment || '');
      }
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    if (idToken) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, id]);

  const openAnswerModal = (answer) => {
    setPendingAnswer(answer);
    setModalOpen(true);
  };

  const submitRating = async () => {
    if (!pendingAnswer) return;
    setBusy(true);
    try {
      const wantsToContinue = pendingAnswer === 'yes';
      await rateSample(idToken, id, {
        wantsToContinue,
        contactOptIn: wantsToContinue && contactOptIn,
        comment: comment || undefined,
        readSeconds: Math.round((Date.now() - startedAt.current) / 1000),
      });
      await load();
      setModalOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeContact = async () => {
    setBusy(true);
    try {
      await revokeContact(idToken, id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!sample && !error) return <div role="status">Loading sample chapter…</div>;
  if (error && !sample) return <Alert variant="error">{error}</Alert>;
  if (!sample) return null;

  const myRating = sample.myRating;
  const optedIn = myRating && myRating.contactOptIn && !myRating.contactRevokedAt;

  return (
    <Page>
      <Link className="br-button br-button--secondary br-button--small" to="/sample-chapters/discover">
        ← Back to samples
      </Link>
      {error && <Alert variant="error">{error}</Alert>}
      <h1>{sample.title}</h1>
      <p>
        by {sample.authorName || 'Unknown'}{' '}
      </p>
      <Tags>
        <Tag>Variant {sample.variantLabel}</Tag>
        <Tag>Text</Tag>
        {sample.audioUrl ? (
          <Tag>Audio</Tag>
        ) : (
          <Tag>No audio</Tag>
        )}
      </Tags>
      {sample.description && <p>{sample.description}</p>}

      {sample.audioUrl && (
        <Section>
          <strong>Listen</strong>
          <audio controls src={sample.audioUrl} style={{width: '100%'}} />
        </Section>
      )}

      <ChapterText>{sample.contentText}</ChapterText>

      {myRating ? (
        <Section>
          <h3>Your rating</h3>
          <p>
            You said{' '}
            <strong>
              {myRating.wantsToContinue ? 'yes' : 'no'}
            </strong>{' '}
            — {myRating.wantsToContinue
              ? 'you would keep reading.'
              : 'you would not keep reading.'}
          </p>
          {optedIn && (
            <Alert variant="info">
              You agreed to be contacted by the author when more material is
              ready. Your contact information will be shared with the author.{' '}
              <Button variant="secondary" size="small" onClick={handleRevokeContact} disabled={busy}>
                Revoke and remove my contact
              </Button>
            </Alert>
          )}
          {!optedIn && myRating.contactRevokedAt && (
            <Alert>You revoked your contact opt-in.</Alert>
          )}
          <Button variant="secondary" size="small" onClick={() => openAnswerModal(myRating.wantsToContinue ? 'yes' : 'no')}>
            Update rating
          </Button>
        </Section>
      ) : (
        <StickyCta>
          <strong>Would you keep reading?</strong>
          <div style={{marginTop: 8}}>
            <Button onClick={() => openAnswerModal('yes')}>
              Yes
            </Button>
            <Button variant="secondary" onClick={() => openAnswerModal('no')}>
              No
            </Button>
          </div>
        </StickyCta>
      )}

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={pendingAnswer === 'yes'
          ? 'Glad you would keep reading!'
          : 'Thanks for your honest answer'}
        actions={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submitRating} disabled={busy}>
            {busy ? 'Saving…' : 'Submit'}
          </Button>
        </>}
      >
          {pendingAnswer === 'yes' && (
            <AnswerForm>
              <div>
                <label>
                  <input type="checkbox"
                  checked={contactOptIn}
                  onChange={event => setContactOptIn(event.target.checked)}
                  />{' '}Yes, let the author contact me when more material is ready
                </label>
                <Note>
                  * If you opt in, your contact information (name and email
                  associated with your account) will be shared with the author
                  of this sample. You can revoke at any time from this page.
                </Note>
              </div>
              <label>Optional comment to the author
                <textarea
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </label>
            </AnswerForm>
          )}
          {pendingAnswer === 'no' && (
            <AnswerForm>
              <label>Optional: tell the author why (won't be shown publicly)
                <textarea
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </label>
            </AnswerForm>
          )}
      </Dialog>
    </Page>
  );
};

export default SampleChapterReader;
